import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import Header from "@/components/app/Header";
import { useSeo } from "@/lib/seo";
import { ArrowRight, Film, Users, Sparkles, Share2 } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1511988617509-a57c8a288659?auto=format&fit=crop&w=1600&q=80";
const WEDDING = "https://images.pexels.com/photos/33051754/pexels-photo-33051754.jpeg?auto=compress&cs=tinysrgb&w=1200";
const FAREWELL = "https://images.unsplash.com/photo-1511632765486-a01980e01a18?auto=format&fit=crop&w=900&q=80";
const MEMORIAL = "https://images.unsplash.com/photo-1499568509606-4f9b771232ed?auto=format&fit=crop&w=900&q=80";

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08, delayChildren: 0.1 } },
};
const fade = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

export default function Landing() {
  useSeo({
    title: "Chapter — A story told by everyone",
    description: "Chapter turns short video messages from friends and family into one cinematic tribute film — for goodbyes, weddings, birthdays, retirements and the moments that matter.",
    path: "/",
  });

  useEffect(() => {
    const existing = document.getElementById("chapter-jsonld");
    if (existing) existing.remove();
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.id = "chapter-jsonld";
    s.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: "Chapter",
      applicationCategory: "MultimediaApplication",
      operatingSystem: "Web",
      description:
        "Chapter turns short video messages from friends and family into one cinematic tribute film — for goodbyes, weddings, birthdays, retirements and the moments that matter.",
      url: "https://chptr.org/",
      offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
      creator: { "@type": "Organization", name: "Chapter" },
    });
    document.head.appendChild(s);
    return () => { s.remove(); };
  }, []);

  return (
    <div className="min-h-screen bg-[var(--em-bg)] text-[var(--em-ink)]">
      <Header />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-20 md:pb-28">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-end">
            <motion.div variants={stagger} initial="hidden" animate="show" className="lg:col-span-7">
              <motion.p variants={fade} className="font-mono-label text-[var(--em-clay)]">Collaborative memory film</motion.p>
              <motion.h1 variants={fade} className="font-serif-em mt-5 text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
                A goodbye isn't <em className="not-italic text-[var(--em-clay)]">one moment</em> —<br className="hidden md:block" />
                it's a story told by everyone.
              </motion.h1>
              <motion.p variants={fade} className="mt-7 text-lg md:text-xl text-[var(--em-ink-soft)] max-w-xl leading-relaxed">
                Chapter turns short video messages from friends and family into one cinematic tribute —
                for goodbyes, weddings, birthdays, retirements, and the moments that matter.
              </motion.p>
              <motion.div variants={fade} className="mt-9 flex flex-wrap items-center gap-4">
                <Link to="/signup">
                  <Button data-testid="hero-cta-start" className="h-12 px-7 rounded-full bg-[var(--em-clay)] hover:bg-[var(--em-clay-deep)] text-white text-base btn-lift">
                    Create an event <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/login">
                  <Button variant="ghost" data-testid="hero-cta-signin" className="h-12 px-5 rounded-full text-base text-[var(--em-ink)] hover:bg-[var(--em-line)]/60">I've been invited</Button>
                </Link>
              </motion.div>
              <motion.div variants={fade} className="mt-10 flex items-center gap-6 text-sm text-[var(--em-ink-soft)]">
                <div className="flex -space-x-2">
                  {[FAREWELL, MEMORIAL, WEDDING].map((src, i) => (
                    <span key={i} className="w-9 h-9 rounded-full border-2 border-[var(--em-bg)] overflow-hidden bg-[var(--em-line)]">
                      <img src={src} alt="" className="w-full h-full object-cover" />
                    </span>
                  ))}
                </div>
                <span>No app downloads. Contributors join with one link.</span>
              </motion.div>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.2 }}
              className="lg:col-span-5 relative">
              <div className="relative rounded-[28px] overflow-hidden border border-[var(--em-line)] shadow-[0_40px_80px_-40px_rgba(28,25,23,0.35)]">
                <img src={HERO} alt="Friends laughing at sunset" className="w-full h-[480px] object-cover" />
                <div className="absolute inset-0 bg-gradient-to-tr from-black/40 via-black/10 to-transparent" />
                <div className="absolute left-5 bottom-5 right-5 flex items-center justify-between">
                  <div className="text-white">
                    <p className="font-mono-label opacity-90">Now playing</p>
                    <p className="font-serif-em text-2xl mt-1">Marilyn's Going Away Party</p>
                  </div>
                  <span className="px-3 py-1 rounded-full bg-white/90 text-[var(--em-ink)] text-xs font-medium">14 voices</span>
                </div>
              </div>
              <div className="absolute -bottom-7 -left-7 w-32 h-32 rounded-2xl overflow-hidden border border-[var(--em-line)] shadow-xl hidden md:block">
                <img src={WEDDING} alt="" className="w-full h-full object-cover" />
              </div>
              <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full overflow-hidden border-4 border-[var(--em-bg)] shadow-xl hidden md:block">
                <img src={FAREWELL} alt="" className="w-full h-full object-cover" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-24">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
          <div>
            <p className="font-mono-label text-[var(--em-sage)]">How it works</p>
            <h2 className="font-serif-em text-3xl md:text-5xl mt-3 max-w-2xl leading-tight">Many voices, one cinematic film.</h2>
          </div>
          <p className="max-w-md text-[var(--em-ink-soft)] leading-relaxed">
            Built for goodbyes, weddings, birthdays, retirements and memorials. Each guest contributes a moment — Chapter stitches the story.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            { icon: Film, label: "01", title: "Create an event", body: "Name it, pick a theme, write a few prompts. Done in a minute." },
            { icon: Share2, label: "02", title: "Share one link", body: "Friends and family contribute from any phone. No accounts." },
            { icon: Users, label: "03", title: "Collect voices", body: "Short videos, photos, notes — answered to your prompts." },
            { icon: Sparkles, label: "04", title: "Play the film", body: "Reorder clips, add intro & outro, share a cinematic reel." },
          ].map((step, i) => (
            <motion.div key={i}
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              className="soft-card relative bg-[var(--em-surface)] border border-[var(--em-line)] rounded-2xl p-6 h-full">
              <span className="font-mono-label text-[var(--em-ink-soft)]">{step.label}</span>
              <step.icon className="mt-6 text-[var(--em-clay)]" size={28} strokeWidth={1.6} />
              <h3 className="font-serif-em text-xl mt-4">{step.title}</h3>
              <p className="text-sm text-[var(--em-ink-soft)] mt-2 leading-relaxed">{step.body}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* MOMENTS BENTO */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-24">
        <div className="mb-10">
          <p className="font-mono-label text-[var(--em-clay)]">Moments people make on Chapter</p>
          <h2 className="font-serif-em text-3xl md:text-5xl mt-3 max-w-2xl leading-tight">Every story is worth keeping.</h2>
        </div>
        <div className="grid grid-cols-6 grid-rows-2 gap-4 h-[520px]">
          <div className="col-span-3 row-span-2 relative rounded-2xl overflow-hidden border border-[var(--em-line)]">
            <img src={WEDDING} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
            <div className="absolute bottom-5 left-5 right-5 text-white">
              <p className="font-mono-label opacity-90">Weddings</p>
              <p className="font-serif-em text-2xl mt-1">"A toast from everyone who shaped us."</p>
            </div>
          </div>
          <div className="col-span-3 row-span-1 relative rounded-2xl overflow-hidden border border-[var(--em-line)]">
            <img src={FAREWELL} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-transparent" />
            <div className="absolute bottom-4 left-5 text-white">
              <p className="font-mono-label opacity-90">Farewells</p>
              <p className="font-serif-em text-xl mt-1">Goodbyes that last beyond the day.</p>
            </div>
          </div>
          <div className="col-span-3 row-span-1 relative rounded-2xl overflow-hidden border border-[var(--em-line)]">
            <img src={MEMORIAL} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-transparent" />
            <div className="absolute bottom-4 left-5 text-white">
              <p className="font-mono-label opacity-90">Tributes</p>
              <p className="font-serif-em text-xl mt-1">A living scrapbook of voices.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-24">
        <div className="relative rounded-[28px] overflow-hidden bg-[var(--em-night)] text-white p-10 md:p-16 grain">
          <div className="relative max-w-3xl">
            <p className="font-mono-label text-[var(--em-clay)]">Begin a memory</p>
            <h3 className="font-serif-em text-3xl md:text-5xl mt-4 leading-tight">
              Some moments deserve more than a group chat.
            </h3>
            <p className="mt-5 text-white/75 text-lg leading-relaxed">
              Send one link. Collect the voices that matter. Watch them become one film.
            </p>
            <Link to="/signup">
              <Button data-testid="cta-bottom-start" className="mt-8 h-12 px-7 rounded-full bg-[var(--em-clay)] hover:bg-[var(--em-clay-deep)] text-white text-base btn-lift">
                Start your event <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-[var(--em-line)]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-12">
          <div className="grid md:grid-cols-3 gap-10">
            <div>
              <span className="font-serif-em text-2xl text-[var(--em-ink)]">Chapter</span>
              <p className="text-sm text-[var(--em-ink-soft)] mt-3 leading-relaxed">
                A collaborative memory-making platform for milestones — weddings, birthdays, retirements, farewells, memorials and celebrations. One shared film, many voices.
              </p>
            </div>
            <div>
              <p className="font-mono-label text-[var(--em-clay)]">Perfect for</p>
              <ul className="mt-3 space-y-1.5 text-sm text-[var(--em-ink-soft)]">
                <li>Going-away parties &amp; farewell tributes</li>
                <li>Wedding rehearsal dinners &amp; toast reels</li>
                <li>Milestone birthdays &amp; retirements</li>
                <li>Memorials &amp; celebrations of life</li>
                <li>Reunions &amp; end-of-year team wraps</li>
              </ul>
            </div>
            <div>
              <p className="font-mono-label text-[var(--em-sage)]">How it works</p>
              <ol className="mt-3 space-y-1.5 text-sm text-[var(--em-ink-soft)] list-decimal list-inside">
                <li>Create an event with a title and prompts</li>
                <li>Share one link with friends and family</li>
                <li>Each guest records a short message — no login</li>
                <li>Reorder clips, add music, play the reel</li>
                <li>Download HD film + a printable keepsake book</li>
              </ol>
            </div>
          </div>
          <div className="mt-10 pt-6 border-t border-[var(--em-line)] flex flex-wrap items-center justify-between text-sm text-[var(--em-ink-soft)]">
            <span>© {new Date().getFullYear()} Chapter · A story told by everyone.</span>
            <span>Made for the moments that matter.</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
